import { createContext, useContext, useEffect, useState, useCallback } from "react";
import adminAxios from "../admin/adminAxios"; 
import socket from "../socket"; 

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    try {
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      return null;
    }
  });
  
  const [loading, setLoading] = useState(true);

  /**
   * TERMINATE SESSION
   */
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("user");
    localStorage.removeItem("adminId");
    
    if (socket) {
      socket.emit("logout"); 
      socket.disconnect();
    }
    
    setUser(null);
    window.location.href = "/login";
  }, []);

  /**
   * REFRESH PROFILE DATA
   */
  const fetchProfile = useCallback(async () => {
    try {
      const res = await adminAxios.get("/user/profile", {
        headers: {
          "x-ops-handshake": "maritime_ops_secure_2026",
          "x-admin-secret": "maritime_ops_secure_2026",
          "Authorization": `Bearer ${localStorage.getItem("token") || localStorage.getItem("adminToken")}`
        }
      });
      
      const userData = res.data.user || res.data;
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
      if (userData.adminId) {
        localStorage.setItem("adminId", userData.adminId);
      }
      return userData;
    } catch (err) {
      console.error("[AUTH] Profile Sync Error:", err.message);
      const status = err.response?.status;
      const errorMessage = err.response?.data?.msg || err.response?.data?.message || "";

      if (status === 401) {
        logout();
      }

      if (status === 403) {
        const isAccountRevoked = 
          errorMessage.toLowerCase().includes("revoked") || 
          errorMessage.toLowerCase().includes("disabled") ||
          errorMessage.toLowerCase().includes("session terminated");

        if (isAccountRevoked) {
          logout();
        }
      }
      throw err;
    }
  }, [logout]);

  /**
   * PATCH LOCAL USER STATE
   */
  const updateUserData = useCallback((newData) => {
    setUser((prev) => {
      if (!prev) return null;
      const updated = {
        ...prev,
        ...newData,
        transactionHistory: newData.transactionHistory || prev.transactionHistory || []
      };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  /**
   * INITIALIZE SESSION
   */
  const login = useCallback((data) => {
    const tokenToStore = data.token;
    const userData = data.user;

    localStorage.setItem("token", tokenToStore);
    localStorage.setItem("user", JSON.stringify(userData));
    
    if (userData.role === "admin") {
      localStorage.setItem("adminToken", tokenToStore);
      if (userData.adminId) {
        localStorage.setItem("adminId", userData.adminId);
      }
    }

    setUser(userData);

    if (socket) {
      if (!socket.connected) socket.connect();
      
      // Tenant-aware socket join payload
      const joinPayload = userData.role === "admin" 
        ? { role: "admin", adminId: userData.adminId || userData._id } 
        : String(userData._id);
        
      socket.emit("join", joinPayload);
    }
  }, []);

  /**
   * AUTHENTICATION PERSISTENCE
   */
  useEffect(() => {
    let isSubscribed = true;
    const initializeAuth = async () => {
      const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
      
      if (!token) {
        if (isSubscribed) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        await fetchProfile();
      } catch (e) {
        console.warn("[AUTH] Initial sync failed, using cached data.");
      } finally {
        if (isSubscribed) setLoading(false);
      }
    };

    initializeAuth();
    return () => { isSubscribed = false; };
  }, [fetchProfile]);

  /**
   * REAL-TIME TELEMETRY ENGINE
   */
  useEffect(() => {
    if (user?._id && socket) {
      
      const onConnect = () => {
        const joinPayload = user.role === "admin" 
          ? { role: "admin", adminId: user.adminId || user._id } 
          : String(user._id);

        console.log(`%c[AUTH] Uplink Sync: Joining as tenant scope`, "color: #3b82f6; font-weight: bold;");
        socket.emit("join", joinPayload);
      };

      if (socket.connected) {
        onConnect();
      } else {
        socket.connect();
      }

      socket.on("connect", onConnect);

      const onWalletUpdate = (data) => {
        if (data.newBalance !== undefined) {
          updateUserData({ 
            creditBalance: data.newBalance,
            balance: data.newBalance 
          });
        }
      };

      const handleAccountStatus = (data) => updateUserData(data);

      socket.on("walletUpdate", onWalletUpdate);
      socket.on("accountStatusUpdate", handleAccountStatus);
      socket.on("registryRefresh", fetchProfile);
      socket.on("transactionUpdate", fetchProfile);

      return () => {
        socket.off("connect", onConnect);
        socket.off("walletUpdate", onWalletUpdate);
        socket.off("accountStatusUpdate", handleAccountStatus);
        socket.off("registryRefresh", fetchProfile);
        socket.off("transactionUpdate", fetchProfile);
      };
    }
  }, [user?._id, user?.role, user?.adminId, updateUserData, fetchProfile]);

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        userId: user?._id, 
        adminId: user?.adminId,
        login, 
        logout, 
        loading, 
        socket, 
        updateUserData, 
        fetchProfile 
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};